import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  FileUp,
  SendHorizonal,
  FileText,
  BarChart3,
  Menu,
  LogOut,
} from 'lucide-react'
import { useAuth } from '@/context/auth'

const navItems = [
  {
    to: '/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    isActive: (pathname: string) => pathname === '/dashboard',
  },
  {
    to: '/intake',
    label: 'Intake',
    icon: FileUp,
    isActive: (pathname: string) => pathname === '/intake' || pathname === '/cases/new',
  },
  {
    to: '/transmit',
    label: 'Transmit',
    icon: SendHorizonal,
    isActive: (pathname: string) => pathname === '/transmit',
  },
  {
    to: '/cases',
    label: 'Cases',
    icon: FileText,
    isActive: (pathname: string) => pathname === '/cases' || (/^\/cases\/[^/]+$/.test(pathname) && pathname !== '/cases/new'),
  },
  {
    to: '/reports',
    label: 'Reports',
    icon: BarChart3,
    isActive: (pathname: string) => pathname === '/reports',
  },
]

export function AppShell() {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const [navCollapsed, setNavCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.sessionStorage.getItem('vice-nav-collapsed') === 'true'
  })

  useEffect(() => {
    window.sessionStorage.setItem('vice-nav-collapsed', String(navCollapsed))
  }, [navCollapsed])

  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-primary focus:px-3 focus:py-2 focus:rounded focus:shadow"
      >
        Skip to main content
      </a>

      <div className="h-1 bg-vice-sunset" aria-hidden="true" />

      <header className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/logo-invert.png" alt="VICE" className="h-9 w-auto" />
            <span className="hidden lg:inline text-xs font-semibold text-neutral-100 max-w-[18ch] leading-snug border-l border-white/20 pl-4">
              Virgin Islands Commercial Environment
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden lg:inline text-neutral-200">{user?.email}</span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex items-center gap-1 rounded border border-white/30 px-3 py-1.5 hover:bg-white/10"
            >
              <LogOut size={16} aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        <nav
          aria-label="Primary"
          className={`hidden border-r border-neutral-200 bg-white transition-[width] duration-200 md:block ${
            navCollapsed ? 'w-20' : 'w-60'
          }`}
        >
          <ul className="space-y-1 p-3">
            <li className="hidden md:block">
              <button
                type="button"
                onClick={() => setNavCollapsed((current) => !current)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 bg-white text-primary shadow-sm transition hover:bg-neutral-50"
                aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'}
              >
                <Menu size={18} aria-hidden="true" />
              </button>
            </li>
            {navItems.map(({ to, label, icon: Icon, isActive }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  className={() =>
                    `group flex items-center ${navCollapsed ? 'justify-center px-2' : 'gap-2 px-3'} rounded-lg py-2.5 text-sm font-medium ${
                      isActive(location.pathname)
                        ? 'bg-secondary-lightest text-secondary'
                        : 'text-neutral-700 hover:bg-neutral-50'
                    }`
                  }
                  aria-label={label}
                  title={navCollapsed ? label : undefined}
                >
                  <Icon size={18} aria-hidden="true" />
                  {navCollapsed ? <span className="sr-only">{label}</span> : label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main id="main" className="flex-1 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6">
            <Outlet />
          </div>
        </main>
      </div>

      <footer className="bg-white border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 text-sm text-neutral-500 sm:px-6">
          &copy; {new Date().getFullYear()} VICE &middot; Built by{' '}
          <a
            href="https://metaphase.tech"
            target="_blank"
            rel="noreferrer"
            className="text-accent-sunset font-bold hover:text-accent-coral"
          >
            MetaPhase
          </a>
        </div>
      </footer>
    </div>
  )
}
